import { Icon, ListItemButton, ListItemIcon, Tooltip, Typography } from '@mui/material'
import classNames from 'classnames'
import i18n from 'i18next'
import React, { ReactElement, useMemo } from 'react'
import { useLocation } from 'react-router'
import authStore from 'store/auth/auth'
import { FILTER_QUERYSTRING_PARAM, GROUPING_QUERYSTRING_PARAM } from 'store/filter/urlFilter.helper'
import { Route } from 'ui/components/Navigation/types'
import StyledLink from 'ui/components/StyledLink/StyledLink'
import { useModuleIsActivatedOnSystem } from 'ui/layouts/MainLayout/MainLayout.utils'
import { useSnapshot } from 'valtio'
import styles from './SidebarItem.module.less'

const defaultLanguage = 'en'
const sidebarItemIconSx = { minWidth: 40 }
const sidebarItemLabelSx = { minWidth: 0, flex: 1, display: 'block' }

type SidebarItemProps = {
  item: Route
  active: boolean
  nested?: boolean
  sideBarOpen: boolean
  onNavigate?: () => void
}

function getTooltipForSideBarItem(
  sideBarOpen: boolean,
  moduleName: string,
  moduleIsInactiveForSystem: boolean
): string {
  if (sideBarOpen) {
    if (moduleIsInactiveForSystem) {
      return i18n.t(
        `This module is not active on this system. You're only seeing this item in the menu because you are a super user.`
      )
    }
    return ''
  } else {
    if (moduleIsInactiveForSystem) {
      return `${moduleName} - ${i18n.t("This module is not active on this system. You're only seeing this item in the menu because you are a super user.")}`
    }
    return moduleName
  }
}

function SidebarItem({ item, active, nested, sideBarOpen, onNavigate }: SidebarItemProps): ReactElement {
  const lang = localStorage.getItem('language') ?? defaultLanguage

  const location = useLocation()
  const { isSuperUser } = useSnapshot(authStore)
  const moduleIsInactiveForSystem = !useModuleIsActivatedOnSystem(item.module) && isSuperUser

  const linkTo = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const basePath = item.path.split(`:`, 1)[0]

    // Only preserve 'filter' and 'group' query params when linking between modules
    const allowedParams = new URLSearchParams()
    const filterVal = searchParams.get(FILTER_QUERYSTRING_PARAM)

    if (filterVal) {
      allowedParams.set(FILTER_QUERYSTRING_PARAM, filterVal)
    }

    const groupVal = searchParams.get(GROUPING_QUERYSTRING_PARAM)
    if (groupVal) {
      allowedParams.set(GROUPING_QUERYSTRING_PARAM, groupVal)
    }

    const queryString = allowedParams.toString()
    const hash = location.hash ?? ''

    if (!queryString) {
      return `${basePath}${hash}`
    }

    return `${basePath}?${queryString}${hash}`
  }, [item, location.search, location.hash])
  const tooltipTitle = getTooltipForSideBarItem(sideBarOpen, item.sidebarName ?? '', moduleIsInactiveForSystem)
  return (
    <Tooltip arrow placement="right" title={tooltipTitle}>
      <li lang={lang}>
        <StyledLink to={linkTo} onClick={onNavigate}>
          <ListItemButton
            key={item.sidebarName}
            className={classNames(styles.SidebarItem_ListItem, {
              [styles.SidebarItem_ActiveListItem]: active,
              [styles.SidebarItem_Nested]: nested,
              [styles.SidebarItem_ActiveNested]: nested && active,
              [styles.SidebarItem_InactiveForSystem]: isSuperUser && moduleIsInactiveForSystem,
            })}
          >
            {item.icon ? (
              <ListItemIcon className={styles.SidebarItem_Text} sx={sidebarItemIconSx}>
                {typeof item.icon === `string` ? (
                  <Icon
                    className={classNames(item.icon, {
                      [styles.SidebarItem_NestedText]: nested,
                    })}
                  />
                ) : (
                  <item.icon />
                )}
              </ListItemIcon>
            ) : null}
            {sideBarOpen && (
              <Typography
                lang={lang}
                sx={sidebarItemLabelSx}
                className={classNames(styles.SidebarItem_Label, { [styles.SidebarItem_NestedText]: nested })}
                variant="h5"
              >
                {nested && item.sidebarNameAsChild ? item.sidebarNameAsChild : (item.sidebarName ?? item.sidebarName)}
              </Typography>
            )}
          </ListItemButton>
        </StyledLink>
      </li>
    </Tooltip>
  )
}

export default React.memo(SidebarItem, (prev, next) => {
  return prev.active === next.active && prev.sideBarOpen === next.sideBarOpen
})
